// ============================================================
// Pages légales — CGU, CGV, Mentions légales, Politique de confidentialité
// Routes Studio uniquement (.merlin-content-ai.com) :
//   /cgu                 → CGU
//   /cgv                 → CGV
//   /mentions-legales    → Mentions légales
//   /confidentialite     → Politique de confidentialité
//
// Rédaction adaptée SaaS B2C avec essai gratuit 7 jours + abonnement Stripe
// auto-renouvelable. RGPD inclus. Juridiction française.
//
// ⚠️ Avant production, complète les placeholders [À COMPLÉTER] :
//   - Forme sociale + raison sociale (ex: "EI Antonin Girardeau" ou "SAS Merlin")
//   - Numéro SIREN/SIRET
//   - Adresse postale du siège
//   - Numéro TVA intracommunautaire (si assujetti)
//   - Numéro RCS (si société)
// ============================================================

import { useEffect } from 'react'

// Données légales centralisées (modifiables en un seul endroit)
const LEGAL_INFO = {
  editorName: 'AUDAX',
  editorForm: 'EURL (Entreprise Unipersonnelle à Responsabilité Limitée)',
  editorSiret: 'SIREN 101 471 605, RCS Paris',
  editorAddress: '60 rue François 1er, 75008 Paris',
  editorEmail: 'hello@merlin-content-ai.com',
  publicationDirector: 'Antonin Girardeau, gérant',
  hostingProvider: 'Railway Corp.',
  hostingAddress: '548 Market St #74287, San Francisco, CA 94104, États-Unis',
  hostingWebsite: 'https://railway.app',
  databaseProvider: 'Supabase Inc. (Postgres hébergé en UE)',
  productName: 'Merlin Studio',
  productUrl: 'https://studio.merlin-content-ai.com',
  paymentProvider: 'Stripe Payments Europe Ltd.',
  emailProvider: 'Resend Inc.',
  lastUpdate: '30 juillet 2026',
}

// ─── Layout commun ─────────────────────────────────────────────
function LegalLayout({ title, subtitle, children }) {
  useEffect(() => { document.title = `${title} | Merlin Studio` }, [title])
  return (
    <div className="min-h-screen text-ink"
      style={{ background: 'linear-gradient(180deg, #fffbf5 0%, #faf8f5 100%)' }}>
      <header className="border-b border-[#f1e9dc] bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-sm font-bold text-ink hover:text-orange-700 transition-colors">
            <span style={{
              background: 'linear-gradient(135deg, #fe8204, #fbbf24)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>Merlin Studio</span>
          </a>
          <a href="/" className="text-xs text-ink-muted hover:text-ink underline">← Retour</a>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
        <h1 className="text-2xl sm:text-3xl font-bold text-ink mb-2 leading-tight tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-ink-muted mb-8">{subtitle}</p>}
        <div className="prose-merlin">{children}</div>
      </main>
      <footer className="border-t border-[#f1e9dc] py-6 mt-10">
        <div className="max-w-3xl mx-auto px-5 text-[11px] text-ink-muted flex flex-wrap gap-x-5 gap-y-2">
          <a href="/cgu" className="hover:text-ink underline">CGU</a>
          <a href="/cgv" className="hover:text-ink underline">CGV</a>
          <a href="/confidentialite" className="hover:text-ink underline">Confidentialité</a>
          <a href="/mentions-legales" className="hover:text-ink underline">Mentions légales</a>
          <a href={`mailto:${LEGAL_INFO.editorEmail}`} className="hover:text-ink underline">Contact</a>
          <span className="ml-auto">© 2026 Merlin Studio</span>
        </div>
      </footer>
      <style>{`
        .prose-merlin h2 { font-size: 18px; font-weight: 700; color: #1a1040; margin: 28px 0 10px; letter-spacing: -0.01em; }
        .prose-merlin h3 { font-size: 15px; font-weight: 700; color: #1a1040; margin: 22px 0 8px; }
        .prose-merlin p { font-size: 14px; line-height: 1.7; color: #2d1f35; margin: 0 0 12px; }
        .prose-merlin ul { font-size: 14px; line-height: 1.7; color: #2d1f35; margin: 0 0 14px; padding-left: 22px; }
        .prose-merlin li { margin-bottom: 4px; }
        .prose-merlin strong { color: #1a1040; }
        .prose-merlin a { color: #d97206; text-decoration: underline; }
        .prose-merlin a:hover { color: #9a3412; }
        .prose-merlin .callout { background: #fff7ed; border-left: 3px solid #fe8204; padding: 12px 16px; border-radius: 8px; margin: 14px 0; font-size: 13.5px; }
      `}</style>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// CGU — Conditions Générales d'Utilisation
// ═══════════════════════════════════════════════════════════════
export function CGU() {
  return (
    <LegalLayout title="Conditions Générales d'Utilisation" subtitle={`Dernière mise à jour : ${LEGAL_INFO.lastUpdate}`}>
      <p>Les présentes Conditions Générales d'Utilisation (« <strong>CGU</strong> ») régissent l'accès et l'usage du service <strong>{LEGAL_INFO.productName}</strong> (ci-après le « <strong>Service</strong> »), édité par <strong>{LEGAL_INFO.editorName}</strong> ({LEGAL_INFO.editorForm}), accessible à l'adresse <a href={LEGAL_INFO.productUrl}>{LEGAL_INFO.productUrl}</a>.</p>
      <p>L'utilisation du Service implique l'acceptation pleine et entière des présentes CGU. L'Utilisateur qui n'accepte pas ces conditions doit cesser immédiatement d'utiliser le Service.</p>

      <h2>1. Objet du Service</h2>
      <p>Merlin Studio est un assistant d'intelligence artificielle destiné aux créateurs de contenu sur les réseaux sociaux. Il propose notamment :</p>
      <ul>
        <li>L'analyse automatisée de comptes Instagram publics</li>
        <li>La génération de scripts de vidéos courtes adaptés au positionnement de l'Utilisateur</li>
        <li>L'analyse de performances de vidéos individuelles</li>
        <li>L'accès à une bibliothèque de formats viraux référencés</li>
      </ul>

      <h2>2. Accès au Service</h2>
      <p>Le Service est accessible 24h/24, 7j/7, sous réserve de maintenance ou de cas de force majeure. L'Éditeur ne saurait être tenu responsable des interruptions techniques ponctuelles.</p>
      <p>L'accès nécessite la création d'un compte personnel (email + mot de passe ou connexion Google). L'Utilisateur garantit l'exactitude des informations communiquées et s'engage à maintenir la confidentialité de ses identifiants.</p>
      <p>L'accès aux fonctionnalités complètes peut être conditionné à la souscription d'un abonnement payant régi par les Conditions Générales de Vente (CGV).</p>

      <h2>3. Compte Utilisateur</h2>
      <h3>3.1 Création</h3>
      <p>L'Utilisateur doit être âgé d'au moins 18 ans ou disposer de l'autorisation de son représentant légal. Toute information communiquée doit être exacte, à jour, et concerner directement l'Utilisateur.</p>
      <h3>3.2 Sécurité</h3>
      <p>L'Utilisateur est seul responsable de la confidentialité de ses identifiants et de toutes les actions effectuées via son compte. Tout usage frauduleux constaté doit être notifié sans délai à <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a>.</p>
      <h3>3.3 Suppression</h3>
      <p>L'Utilisateur peut supprimer son compte à tout moment depuis son espace personnel. L'Éditeur se réserve le droit de suspendre ou clôturer tout compte en cas de violation des présentes CGU, avec ou sans préavis selon la gravité du manquement.</p>

      <h2>4. Usage du compte Instagram analysé</h2>
      <p>Le Service procède à une lecture seule des comptes Instagram publics analysés via des API tierces autorisées. <strong>Aucune publication, modification ou interaction n'est effectuée au nom de l'Utilisateur sur ses propres comptes</strong>. L'Utilisateur déclare disposer du droit d'analyser les comptes qu'il soumet (le sien et, le cas échéant, ceux dont il a une connaissance publique).</p>
      <p>L'Utilisateur s'engage à ne pas utiliser le Service pour :</p>
      <ul>
        <li>Cibler ou analyser des comptes privés sans autorisation</li>
        <li>Contourner les protections techniques des plateformes tierces (Instagram, TikTok, etc.)</li>
        <li>Reproduire ou redistribuer en masse les analyses générées</li>
      </ul>

      <h2>5. Propriété intellectuelle</h2>
      <h3>5.1 Sur le Service</h3>
      <p>L'ensemble des éléments composant le Service (interface, logos, code source, méthodologie d'analyse, bibliothèque de formats, base de données) est la propriété exclusive de l'Éditeur ou de ses partenaires, et protégé par les lois sur la propriété intellectuelle. Toute reproduction, copie ou exploitation non autorisée est strictement interdite.</p>
      <h3>5.2 Sur le contenu généré</h3>
      <p>L'Utilisateur conserve la pleine propriété des scripts, analyses et contenus générés à partir de ses requêtes via le Service. Il peut les utiliser librement, à des fins personnelles ou commerciales, sans obligation d'attribution.</p>
      <p>L'Éditeur se réserve le droit d'utiliser, sous forme anonymisée et agrégée, les statistiques d'usage pour améliorer le Service (par exemple : popularité des formats, qualité des scripts générés).</p>

      <h2>6. Contenu généré par IA : limites</h2>
      <p>Les contenus produits par Merlin Studio sont générés par des modèles d'intelligence artificielle (Claude d'Anthropic, Gemini de Google). Malgré le soin apporté à la qualité de la pipeline, l'Éditeur ne garantit pas :</p>
      <ul>
        <li>L'exactitude factuelle absolue des informations générées</li>
        <li>L'absence totale de biais</li>
        <li>L'unicité ou l'originalité parfaite des scripts produits</li>
        <li>Le succès commercial ou la performance des contenus publiés par l'Utilisateur</li>
      </ul>
      <p>L'Utilisateur reste seul responsable de la relecture, de l'adaptation et de la publication des contenus générés.</p>

      <h2>7. Comportement de l'Utilisateur</h2>
      <p>L'Utilisateur s'interdit notamment :</p>
      <ul>
        <li>D'utiliser le Service à des fins illicites, frauduleuses ou contraires à l'ordre public</li>
        <li>De tenter d'extraire massivement la base de formats (scraping, automation non autorisée)</li>
        <li>De partager ses identifiants ou de revendre l'accès au Service</li>
        <li>De porter atteinte à l'intégrité technique du Service</li>
        <li>De générer des contenus diffamatoires, haineux, discriminatoires ou portant atteinte aux droits de tiers</li>
      </ul>

      <h2>8. Disponibilité et maintenance</h2>
      <p>L'Éditeur s'efforce de maintenir la disponibilité du Service mais ne peut garantir une disponibilité de 100 %. Des interruptions pour maintenance, mises à jour, ou en raison de défaillances de prestataires tiers peuvent survenir. Aucune indemnité n'est due en cas d'indisponibilité ponctuelle.</p>

      <h2>9. Responsabilité</h2>
      <p>La responsabilité de l'Éditeur ne peut être engagée pour :</p>
      <ul>
        <li>Les dommages indirects (perte de revenus, perte d'audience, atteinte à l'image)</li>
        <li>L'utilisation faite par l'Utilisateur des contenus générés</li>
        <li>Les dysfonctionnements des plateformes tierces (Instagram, Stripe, Google, etc.)</li>
        <li>La perte de données résultant d'un usage non conforme</li>
      </ul>
      <p>En tout état de cause, et dans la limite permise par la loi, la responsabilité totale de l'Éditeur est plafonnée au montant des sommes effectivement versées par l'Utilisateur au cours des 12 derniers mois.</p>

      <h2>10. Données personnelles</h2>
      <p>Le traitement des données personnelles est détaillé dans la <a href="/confidentialite">Politique de Confidentialité</a>, qui fait partie intégrante des présentes CGU.</p>

      <h2>11. Modification des CGU</h2>
      <p>L'Éditeur se réserve le droit de modifier les présentes CGU à tout moment. Les modifications substantielles seront notifiées à l'Utilisateur par email au moins 15 jours avant leur entrée en vigueur. La poursuite de l'usage du Service après cette date vaut acceptation des nouvelles CGU.</p>

      <h2>12. Droit applicable et juridiction</h2>
      <p>Les présentes CGU sont soumises au droit français. En cas de litige, et après tentative de résolution amiable, les tribunaux français compétents seront seuls habilités à trancher le litige, sauf disposition impérative contraire applicable aux Utilisateurs consommateurs.</p>

      <h2>13. Contact</h2>
      <p>Pour toute question relative aux présentes CGU : <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a></p>
    </LegalLayout>
  )
}

// ═══════════════════════════════════════════════════════════════
// CGV — Conditions Générales de Vente
// ═══════════════════════════════════════════════════════════════
export function CGV() {
  return (
    <LegalLayout title="Conditions Générales de Vente" subtitle={`Dernière mise à jour : ${LEGAL_INFO.lastUpdate}`}>
      <p>Les présentes Conditions Générales de Vente (« <strong>CGV</strong> ») régissent la souscription et la facturation des abonnements payants au service <strong>{LEGAL_INFO.productName}</strong>, édité par <strong>{LEGAL_INFO.editorName}</strong> ({LEGAL_INFO.editorForm}), accessible à <a href={LEGAL_INFO.productUrl}>{LEGAL_INFO.productUrl}</a>.</p>
      <p>La souscription d'un abonnement implique l'acceptation pleine et entière des présentes CGV et des <a href="/cgu">CGU</a>.</p>

      <h2>1. Champ d'application</h2>
      <p>Les CGV s'appliquent à toute souscription d'un abonnement payant Merlin Studio par toute personne physique ou morale (ci-après le « <strong>Client</strong> »). Elles prévalent sur tout autre document, sauf accord préalable écrit de l'Éditeur.</p>

      <h2>2. Offres et tarifs</h2>
      <p>Merlin Studio propose plusieurs formules d'abonnement détaillées sur la page tarifs du Service. Les tarifs sont indiqués en euros (€), <strong>toutes taxes comprises (TTC)</strong> lorsque l'Éditeur est assujetti à la TVA, ou hors taxe selon son statut fiscal en vigueur (information disponible sur facture).</p>
      <p>Deux périodicités sont disponibles :</p>
      <ul>
        <li><strong>Mensuelle</strong> : facturée tous les mois à compter de la date de souscription</li>
        <li><strong>Annuelle</strong> : facturée en une seule fois pour 12 mois, avec une remise par rapport au tarif mensuel</li>
      </ul>
      <p>Les tarifs en vigueur lors de la souscription sont applicables pour toute la durée de l'abonnement actif. En cas de résiliation puis de nouvelle souscription ultérieure, les tarifs publics alors en vigueur s'appliqueront.</p>

      <div className="callout">
        <strong>Offre béta, tarif figé à vie</strong> : pendant la période béta, les tarifs sont réduits par rapport au tarif public futur. Tant que le Client maintient son abonnement actif sans interruption, son tarif reste figé. En cas de résiliation, ce tarif n'est plus garanti à la reprise.
      </div>

      <h2>3. Essai gratuit de 7 jours</h2>
      <p>L'Éditeur peut proposer un essai gratuit de 7 jours pour certaines formules. Pour activer l'essai, le Client doit renseigner un moyen de paiement valide. <strong>Aucun prélèvement n'est effectué pendant la durée de l'essai.</strong></p>
      <p>À l'issue de la période d'essai et en l'absence de résiliation, l'abonnement payant démarre automatiquement et le premier prélèvement est effectué le 8ᵉ jour suivant la souscription. Un email de rappel est envoyé au Client 3 jours avant la fin de l'essai.</p>
      <p>Le Client peut annuler son essai à tout moment depuis son espace personnel, sans frais ni justification.</p>

      <h2>4. Souscription et paiement</h2>
      <p>La souscription s'effectue en ligne via la plateforme de paiement <strong>{LEGAL_INFO.paymentProvider}</strong>. Les moyens de paiement acceptés sont la carte bancaire (Visa, Mastercard, American Express) et tout autre moyen ponctuellement proposé par Stripe.</p>
      <p>Le Client garantit qu'il est autorisé à utiliser le moyen de paiement renseigné. Les données bancaires ne transitent ni ne sont stockées par l'Éditeur. Elles sont gérées exclusivement par Stripe, qui répond aux normes de sécurité PCI-DSS.</p>
      <p>Une facture est générée automatiquement à chaque prélèvement et envoyée au Client par email. Elle est également téléchargeable depuis l'espace personnel.</p>

      <h2>5. Reconduction automatique</h2>
      <p>Les abonnements sont à <strong>reconduction tacite</strong>, mensuelle ou annuelle selon la formule choisie. La reconduction donne lieu à un nouveau prélèvement automatique à la date anniversaire de la souscription.</p>
      <p>Conformément à la <strong>loi Chatel</strong>, l'Éditeur informe le Client au moins 30 jours avant la date de reconduction d'un abonnement annuel, lui rappelant son droit de ne pas reconduire.</p>

      <h2>6. Résiliation</h2>
      <h3>6.1 Résiliation par le Client</h3>
      <p>Le Client peut résilier son abonnement <strong>à tout moment</strong>, sans frais ni justification, depuis son espace personnel (section « Abonnement » → « Annuler »).</p>
      <p>La résiliation prend effet à la fin de la période en cours (mensuelle ou annuelle). Le Client conserve l'accès au Service jusqu'à cette date. <strong>Aucun remboursement au prorata n'est effectué pour la période non utilisée</strong>, sauf cas légalement obligatoires.</p>
      <h3>6.2 Résiliation par l'Éditeur</h3>
      <p>L'Éditeur peut résilier l'abonnement d'un Client en cas de :</p>
      <ul>
        <li>Manquement grave aux CGU ou CGV</li>
        <li>Défaut de paiement</li>
        <li>Usage frauduleux ou contraire à l'ordre public</li>
      </ul>
      <p>En cas de résiliation pour faute, aucun remboursement n'est dû.</p>

      <h2>7. Droit de rétractation</h2>
      <p>Conformément aux articles L221-18 et suivants du Code de la consommation, le Client consommateur dispose d'un délai légal de <strong>14 jours</strong> à compter de la conclusion du contrat (souscription en ligne) pour exercer son droit de rétractation, sans avoir à motiver sa décision ni à supporter d'autres coûts. Ce délai légal de 14 jours est <strong>indépendant de la durée de l'essai gratuit</strong>.</p>
      <p>Pendant la période d'essai gratuit (7 jours), aucun prélèvement n'étant effectué, l'exercice du droit de rétractation n'entraîne aucun coût pour le Client. Si le premier prélèvement intervient, au terme de l'essai, alors que le délai légal de rétractation de 14 jours n'est pas expiré, le Client qui exerce son droit de rétractation dans ce délai obtient le <strong>remboursement intégral</strong> de la somme prélevée. Au-delà du 14ᵉ jour suivant la souscription, le délai légal a expiré et le Client peut uniquement résilier son abonnement pour le futur (cf. article 6), sans préjudice de la garantie commerciale prévue à l'article 8.</p>
      <p>Pour exercer son droit de rétractation, le Client peut soit annuler son abonnement depuis son espace personnel, soit envoyer un email à <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a> exprimant sa volonté de se rétracter. Un formulaire-type de rétractation est mis à disposition sur simple demande.</p>

      <h2>8. Garantie de remboursement commerciale</h2>
      <p>Au-delà du délai légal de rétractation, l'Éditeur propose à titre commercial une <strong>garantie satisfait ou remboursé de 14 jours</strong> à compter du premier prélèvement effectif. Pour en bénéficier, le Client adresse une demande motivée à <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a> dans ce délai. Le remboursement est effectué sous 14 jours ouvrés sur le moyen de paiement initial.</p>

      <h2>9. Modification des tarifs</h2>
      <p>L'Éditeur se réserve le droit de modifier ses tarifs publics à tout moment. Toute hausse appliquée à un Client existant lui est notifiée par email au moins 30 jours avant son entrée en vigueur, ce qui lui laisse la possibilité de résilier sans frais s'il refuse la nouvelle grille tarifaire.</p>
      <p>Les Clients béta bénéficient d'un tarif figé tant que leur abonnement est actif et continu (cf. article 2).</p>

      <h2>10. Échec de paiement</h2>
      <p>En cas d'échec d'un prélèvement (carte expirée, fonds insuffisants, opposition), le Client est notifié par email. Stripe procède à plusieurs tentatives automatiques sur une durée de 21 jours maximum. Passé ce délai sans régularisation, l'abonnement est suspendu puis résilié de plein droit, sans indemnité.</p>

      <h2>11. Crédits / quotas mensuels</h2>
      <p>Certaines formules incluent un nombre limité de générations ou d'analyses mensuelles, exprimées en « crédits » ou en quotas d'actions. Ces crédits sont <strong>renouvelés à chaque période de facturation</strong> et <strong>ne sont pas reportables d'une période à l'autre</strong>. Les crédits inutilisés ne donnent lieu à aucun remboursement.</p>

      <h2>12. Données et continuité</h2>
      <p>En cas de résiliation, les données du Client (profil, scripts générés, analyses sauvegardées) restent accessibles depuis son espace personnel pendant <strong>30 jours après la fin de l'abonnement</strong>, période pendant laquelle il peut les exporter. Au-delà, elles sont supprimées de manière définitive sauf obligation légale de conservation.</p>

      <h2>13. Force majeure</h2>
      <p>L'Éditeur ne saurait être tenu responsable de l'inexécution de ses obligations en cas de force majeure au sens de l'article 1218 du Code civil, ni de défaillances d'un prestataire tiers essentiel au Service (hébergeur, fournisseur d'API, processeur de paiement).</p>

      <h2>14. Droit applicable et litiges</h2>
      <p>Les présentes CGV sont régies par le droit français. En cas de litige, le Client est invité à contacter l'Éditeur pour rechercher une solution amiable. À défaut de résolution dans un délai de 60 jours, le Client consommateur peut saisir gratuitement un médiateur de la consommation (coordonnées disponibles sur demande), ou saisir les juridictions compétentes selon les règles de droit commun.</p>

      <h2>15. Contact</h2>
      <p>Pour toute question relative aux présentes CGV ou à votre abonnement : <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a></p>
    </LegalLayout>
  )
}

// ═══════════════════════════════════════════════════════════════
// MENTIONS LÉGALES
// ═══════════════════════════════════════════════════════════════
export function MentionsLegales() {
  return (
    <LegalLayout title="Mentions légales" subtitle={`Dernière mise à jour : ${LEGAL_INFO.lastUpdate}`}>
      <h2>Éditeur du site</h2>
      <p>
        <strong>{LEGAL_INFO.editorName}</strong><br/>
        Forme juridique : {LEGAL_INFO.editorForm}<br/>
        SIREN/SIRET : {LEGAL_INFO.editorSiret}<br/>
        Adresse : {LEGAL_INFO.editorAddress}<br/>
        Email : <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a>
      </p>

      <h2>Directeur de la publication</h2>
      <p>{LEGAL_INFO.publicationDirector}</p>

      <h2>Hébergement</h2>
      <p>
        <strong>{LEGAL_INFO.hostingProvider}</strong><br/>
        {LEGAL_INFO.hostingAddress}<br/>
        Site : <a href={LEGAL_INFO.hostingWebsite} target="_blank" rel="noreferrer">{LEGAL_INFO.hostingWebsite}</a>
      </p>
      <p>Base de données : <strong>{LEGAL_INFO.databaseProvider}</strong>.</p>

      <h2>Propriété intellectuelle</h2>
      <p>L'ensemble du contenu présent sur le site (interface, logos, marques, code, structure, méthodologies d'analyse, base de formats) est protégé par le Code de la propriété intellectuelle. Toute reproduction, représentation, modification ou exploitation, totale ou partielle, est interdite sans autorisation écrite préalable de l'Éditeur.</p>

      <h2>Crédits</h2>
      <p>Les modèles d'IA utilisés sont notamment Claude (Anthropic), Gemini (Google) et text-embedding-3-small (OpenAI). Les paiements sont traités par Stripe. Les emails transactionnels sont envoyés via Resend.</p>

      <h2>Contact</h2>
      <p>Pour toute question : <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a></p>
    </LegalLayout>
  )
}

// ═══════════════════════════════════════════════════════════════
// POLITIQUE DE CONFIDENTIALITÉ (RGPD)
// ═══════════════════════════════════════════════════════════════
export function Confidentialite() {
  return (
    <LegalLayout title="Politique de confidentialité" subtitle={`Dernière mise à jour : ${LEGAL_INFO.lastUpdate}`}>
      <p>La présente politique décrit comment <strong>{LEGAL_INFO.editorName}</strong> (« l'Éditeur ») collecte, utilise et protège les données personnelles des utilisateurs du service <strong>{LEGAL_INFO.productName}</strong>, dans le respect du Règlement (UE) 2016/679 (« <strong>RGPD</strong> ») et de la loi Informatique et Libertés.</p>

      <h2>1. Responsable de traitement</h2>
      <p>Responsable : {LEGAL_INFO.editorName}. Contact : {LEGAL_INFO.editorEmail}</p>

      <h2>2. Données collectées</h2>
      <h3>2.1 Données fournies directement</h3>
      <ul>
        <li><strong>Compte</strong> : email, mot de passe (haché), prénom optionnel, données Google (si OAuth)</li>
        <li><strong>Profil créateur</strong> : pseudo Instagram analysé, niche, audience, objectifs déclarés</li>
        <li><strong>Paiement</strong> : nom et adresse facturation transmis directement à Stripe (jamais stockés par l'Éditeur)</li>
        <li><strong>Communications</strong> : contenu des emails et messages envoyés au support</li>
      </ul>
      <h3>2.2 Données collectées automatiquement</h3>
      <ul>
        <li><strong>Usage</strong> : pages visitées, fonctionnalités utilisées, dates et heures de connexion</li>
        <li><strong>Technique</strong> : type d'appareil, navigateur, langue, résolution d'écran, adresse IP anonymisée</li>
        <li><strong>Cookies tiers</strong> : Meta Pixel, Google Analytics 4 (cf. section Cookies)</li>
      </ul>
      <h3>2.3 Données issues du compte Instagram analysé</h3>
      <p>Lors d'une analyse, des données publiques du compte Instagram fourni sont récupérées (bio, abonnés, dernières publications, vues, engagement). Ces données ne sont collectées que sur action explicite de l'utilisateur et restent strictement liées à son compte.</p>

      <h2>3. Finalités et bases légales</h2>
      <ul>
        <li><strong>Fourniture du Service</strong> (exécution du contrat) : analyses, génération de scripts, suivi des contenus</li>
        <li><strong>Facturation</strong> (exécution du contrat) : prélèvements, factures, comptabilité</li>
        <li><strong>Amélioration du Service</strong> (intérêt légitime) : statistiques d'usage agrégées, débuggage</li>
        <li><strong>Marketing</strong> (consentement) : envoi de communications marketing (désabonnement en 1 clic dans chaque email)</li>
        <li><strong>Obligations légales</strong> : conservation des factures, lutte contre la fraude</li>
      </ul>

      <h2>4. Durée de conservation</h2>
      <ul>
        <li>Compte actif : pendant toute la durée de l'abonnement + 30 jours après résiliation</li>
        <li>Données de facturation : 10 ans (obligation comptable)</li>
        <li>Logs techniques : 12 mois maximum</li>
        <li>Cookies analytics : 13 mois maximum</li>
      </ul>

      <h2>5. Destinataires et sous-traitants</h2>
      <p>L'Éditeur fait appel à des sous-traitants pour héberger et exploiter le Service. Tous sont liés par contrat conforme RGPD :</p>
      <ul>
        <li><strong>Supabase Inc.</strong> (hébergement DB et auth, base en UE)</li>
        <li><strong>Railway Corp.</strong> (hébergement application)</li>
        <li><strong>Anthropic, Google, OpenAI</strong> (modèles IA)</li>
        <li><strong>Stripe</strong> (paiements)</li>
        <li><strong>Resend</strong> (emails transactionnels)</li>
        <li><strong>Meta, Google</strong> (analytics et publicité, via cookies, uniquement avec consentement)</li>
      </ul>
      <p>L'Éditeur ne vend, ne loue ni ne partage les données personnelles à des fins commerciales avec des tiers en dehors de ces sous-traitants nécessaires.</p>

      <h2>6. Transferts hors UE</h2>
      <p>Certains sous-traitants (Anthropic, OpenAI, Railway, Stripe) sont situés aux États-Unis. Les transferts sont encadrés par les clauses contractuelles types adoptées par la Commission européenne (CCT 2021), garantissant un niveau de protection adéquat.</p>

      <h2>7. Vos droits</h2>
      <p>Conformément au RGPD, l'utilisateur dispose des droits suivants :</p>
      <ul>
        <li><strong>Accès</strong> : obtenir copie de ses données</li>
        <li><strong>Rectification</strong> : corriger ses données</li>
        <li><strong>Effacement</strong> (droit à l'oubli) : supprimer son compte et ses données</li>
        <li><strong>Limitation</strong> : restreindre le traitement</li>
        <li><strong>Portabilité</strong> : récupérer ses données dans un format structuré</li>
        <li><strong>Opposition</strong> : refuser certains traitements (notamment marketing)</li>
        <li><strong>Retrait du consentement</strong> à tout moment (cookies, marketing)</li>
      </ul>
      <p>Pour exercer ces droits : <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a> en justifiant de votre identité. Réponse sous 30 jours maximum.</p>
      <p>En cas de désaccord persistant, vous pouvez déposer une réclamation auprès de la <strong>CNIL</strong> (<a href="https://www.cnil.fr/" target="_blank" rel="noreferrer">cnil.fr</a>).</p>

      <h2>8. Cookies et traceurs</h2>
      <p>Le Service utilise les cookies suivants :</p>
      <ul>
        <li><strong>Cookies essentiels</strong> (non soumis à consentement) : session d'authentification, préférences d'affichage</li>
        <li><strong>Cookies analytiques</strong> (soumis à consentement) : Google Analytics 4, Meta Pixel pour mesure d'audience et optimisation publicitaire</li>
      </ul>
      <p>L'utilisateur peut à tout moment refuser ou retirer son consentement via le bandeau cookies ou les paramètres de son navigateur.</p>

      <h2>9. Sécurité</h2>
      <p>L'Éditeur met en œuvre les mesures techniques et organisationnelles appropriées pour protéger les données : chiffrement TLS en transit, mots de passe hachés (bcrypt/argon2), accès restreints, sauvegardes régulières, monitoring d'incidents.</p>
      <p>En cas de violation de données susceptible d'engendrer un risque pour les droits et libertés des utilisateurs, l'Éditeur notifiera la CNIL sous 72 heures et informera les utilisateurs concernés conformément à l'article 34 du RGPD.</p>

      <h2>10. Modifications</h2>
      <p>La présente politique peut être modifiée pour s'adapter aux évolutions légales ou techniques. Toute modification substantielle est notifiée par email aux utilisateurs concernés au moins 15 jours avant son entrée en vigueur.</p>

      <h2>11. Contact</h2>
      <p>Pour toute question relative à la protection de vos données : <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a></p>
    </LegalLayout>
  )
}

// ═══════════════════════════════════════════════════════════════
// RÈGLEMENT JEU-CONCOURS — tirage « 1h de coaching contenu en 1:1 »
// ═══════════════════════════════════════════════════════════════
export function ReglementJeu() {
  return (
    <LegalLayout title="Règlement du tirage au sort" subtitle="« 1h de coaching contenu en 1:1 ». Dernière mise à jour : 7 juillet 2026">
      <h2>1. Organisateur</h2>
      <p>Le tirage au sort est organisé par <strong>{LEGAL_INFO.editorName}</strong> ({LEGAL_INFO.editorForm}, {LEGAL_INFO.editorSiret}), éditrice du service <strong>{LEGAL_INFO.productName}</strong>, accessible à l'adresse <a href={LEGAL_INFO.productUrl}>{LEGAL_INFO.productUrl}</a> (ci-après l'« Organisateur »).</p>

      <h2>2. Participation</h2>
      <p>La participation est ouverte à toute personne majeure disposant d'un compte {LEGAL_INFO.productName}, y compris avec l'offre gratuite. <strong>Aucun achat ni abonnement payant n'est requis pour participer.</strong></p>
      <p>Chaque participant cumule des tickets de participation de deux manières :</p>
      <ul>
        <li><strong>Régularité</strong> : 1 ticket par tranche de 30 jours consécutifs d'utilisation du service (30 jours = 1 ticket, 60 jours = 2 tickets, etc.). Un jour est compté dès une visite de l'application.</li>
        <li><strong>Avis</strong> : 1 ticket (une seule fois par compte) pour avoir répondu au bandeau d'avis « Qu'est-ce qui manque à Merlin pour mieux t'aider ? ».</li>
      </ul>

      <h2>3. Dotation</h2>
      <p>Le lot mis en jeu est <strong>une session de coaching en création de contenu d'une heure, en visioconférence et en tête-à-tête</strong> avec le fondateur de {LEGAL_INFO.productName}. Valeur indicative : 150 € TTC. Le lot est nominatif, non cessible et ne peut faire l'objet d'aucune contrepartie financière.</p>

      <h2>4. Tirage au sort</h2>
      <p>Le tirage est effectué de manière aléatoire parmi l'ensemble des tickets valides, à une fréquence décidée par l'Organisateur (au minimum une fois par trimestre tant que l'opération est active). Plus un participant détient de tickets, plus sa probabilité de gagner est élevée. Un participant ayant déjà gagné est exclu des tirages suivants.</p>
      <p>Le gagnant est contacté par email à l'adresse de son compte dans un délai de 7 jours suivant le tirage. Sans réponse sous 14 jours, un nouveau tirage est effectué.</p>

      <h2>5. Durée et modification</h2>
      <p>L'opération est active à compter du 8 juillet 2026, sans durée déterminée. L'Organisateur se réserve le droit de modifier, suspendre ou mettre fin à l'opération à tout moment ; les tickets déjà acquis restent valides pour le dernier tirage suivant l'annonce de fin.</p>

      <h2>6. Données personnelles</h2>
      <p>Les données utilisées (identifiant de compte, email, jours d'activité) sont traitées conformément à la <a href="/confidentialite">politique de confidentialité</a> du service, aux seules fins de la gestion du tirage.</p>

      <h2>7. Contact</h2>
      <p>Toute question relative à ce règlement peut être adressée à <a href={`mailto:${LEGAL_INFO.editorEmail}`}>{LEGAL_INFO.editorEmail}</a>.</p>
    </LegalLayout>
  )
}

// Helper : détecte si la route actuelle correspond à une page légale
// et renvoie le composant à rendre (null sinon).
export function getLegalPageForCurrentPath() {
  try {
    const path = window.location.pathname.toLowerCase().replace(/\/$/, '')
    if (path === '/cgu') return <CGU />
    if (path === '/cgv') return <CGV />
    if (path === '/confidentialite' || path === '/politique-de-confidentialite') return <Confidentialite />
    if (path === '/mentions-legales' || path === '/mentions') return <MentionsLegales />
    if (path === '/jeu-concours' || path === '/reglement-jeu') return <ReglementJeu />
  } catch { /* fallback */ }
  return null
}
